<template>
  <div v-if="show" class="modal-overlay" @click.self="closeModal">
    <div class="modal-container">
      <div v-if="isSelectingPatient" class="selecting-overlay">
        <div class="spinner" />
      </div>
      <div class="modal-header">
        <h3>Глобальный поиск пациента</h3>
        <button class="close-button" @click="closeModal">
          &times;
        </button>
      </div>

      <div class="modal-body">
        <form @submit.prevent="submitForm" class="request-form">
          <div class="form-group">
            <label for="gps-fio">ФИО</label>
            <input
              id="gps-fio"
              v-model="formData.fio"
              type="text"
              placeholder="Иванов Иван Иванович"
              class="form-input"
            >
          </div>

          <div class="form-group">
            <label for="gps-birth-date">Дата рождения</label>
            <input
              id="gps-birth-date"
              v-model="formData.birth_date"
              type="date"
              :min="minBirthDate"
              :max="maxBirthDate"
              class="form-input"
            >
            <small v-if="birthDateError" class="form-hint form-hint_error">
              {{ birthDateError }}
            </small>
          </div>

          <div class="form-group">
            <label for="gps-gender">Пол</label>
            <select id="gps-gender" v-model="formData.gender" class="form-select">
              <option value="">Не указан</option>
              <option value="m">М</option>
              <option value="f">Ж</option>
            </select>
          </div>

          <div class="form-group">
            <label for="gps-id-patient">ID пациента</label>
            <input
              id="gps-id-patient"
              v-model.number="formData.id_patient"
              type="number"
              min="0"
              step="1"
              placeholder="Например, 12345"
              class="form-input"
            >
          </div>
        </form>

        <div v-if="hasSearched" class="search-results">
          <p v-if="isSearching" class="search-empty">Идёт поиск...</p>
          <template v-else>
            <p v-if="!results.length" class="search-empty">Ничего не найдено</p>
            <table v-else class="results-table">
              <thead>
                <tr>
                  <th>ФИО</th>
                  <th>ID пациента</th>
                  <th>Организация</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in results"
                  :key="`${row.id_patient}_${row.id_nomerok}`"
                  class="results-table__row"
                  @click="selectPatient(row)"
                >
                  <td>{{ row.fio_full }}</td>
                  <td>{{ row.id_patient }}</td>
                  <td>{{ row.org_name }}</td>
                  <td>{{ row.status }}</td>
                </tr>
              </tbody>
            </table>
          </template>
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn-secondary" @click="closeModal">
          Закрыть
        </button>
        <button
          type="submit"
          class="btn-primary"
          :disabled="isSearching || !!birthDateError"
          @click="submitForm"
        >
          Найти
        </button>
      </div>
    </div>
  </div>
</template>

<script>
const GENDER_CODES = {
  m: 1,
  f: 2,
};

const MAX_AGE_YEARS = 120;

function toDateInput(date) {
  return date.toISOString().split('T')[0];
}

export default {
  name: 'GlobalPatientSearchModal',

  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      formData: {
        fio: '',
        birth_date: '',
        gender: '',
        id_patient: '',
      },
      results: [],
      isSearching: false,
      hasSearched: false,
      isSelectingPatient: false,
    };
  },

  computed: {
    maxBirthDate() {
      return toDateInput(new Date());
    },
    minBirthDate() {
      const d = new Date();
      d.setFullYear(d.getFullYear() - MAX_AGE_YEARS);
      return toDateInput(d);
    },
    birthDateError() {
      if (!this.formData.birth_date) return '';
      const selected = new Date(this.formData.birth_date);
      if (selected > new Date()) {
        return 'Дата рождения не может быть в будущем';
      }
      const minDate = new Date();
      minDate.setFullYear(minDate.getFullYear() - MAX_AGE_YEARS);
      if (selected < minDate) {
        return `Возраст не может превышать ${MAX_AGE_YEARS} лет`;
      }
      return '';
    },
  },

  watch: {
    show(newVal) {
      if (newVal) {
        this.resetForm();
      }
    },
  },

  methods: {
    closeModal() {
      this.$emit('close');
    },

    async selectPatient(row) {
      if (this.isSelectingPatient) {
        return;
      }
      this.isSelectingPatient = true;
      try {
        const companies = this.$store.state.user?.companiesList || [];
        const org = companies.find((item) => item.id_org === row.Id_org_customer);
        if (org) {
          await this.$store.dispatch('user/setSelectedCompany', org);
          this.$store.commit('employeesData/UPDATE_GLOBAL_SEARCH', row.fio_full);
        } else {
          console.warn('GlobalPatientSearchModal: организация не найдена для строки', row);
        }
        this.$emit('select-patient', row);
        this.closeModal();
      } finally {
        this.isSelectingPatient = false;
      }
    },

    buildSearchContext() {
      return {
        id_patient: this.formData.id_patient !== '' && this.formData.id_patient !== null
          ? Number(this.formData.id_patient)
          : 0,
        sex: this.formData.gender ? GENDER_CODES[this.formData.gender] : '',
        search_str: this.formData.fio.trim(),
        birthdate: this.formData.birth_date || '',
        show_hiden: 0,
      };
    },

    hasAnyCriteria() {
      return !!(
        this.formData.fio.trim()
        || this.formData.birth_date
        || this.formData.gender
        || (this.formData.id_patient !== '' && this.formData.id_patient !== null)
      );
    },

    async submitForm() {
      if (this.birthDateError) return;

      if (!this.hasAnyCriteria()) {
        this.emitter.emit('message', 'Заполните хотя бы одно поле для поиска');
        return;
      }
      const context = this.buildSearchContext();

      this.hasSearched = true;
      this.isSearching = true;
      try {
        const result = await this.$store.dispatch('employeesData/globalPatientSearch', context);
        this.results = result || [];
      } catch (e) {
        this.results = [];
        this.emitter.emit('message', 'Не удалось выполнить поиск');
      } finally {
        this.isSearching = false;
      }
    },

    resetForm() {
      this.formData = {
        fio: '',
        birth_date: '',
        gender: '',
        id_patient: '',
      };
      this.results = [];
      this.hasSearched = false;
      this.isSearching = false;
      this.isSelectingPatient = false;
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.modal-container {
  position: relative;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease;
}

.selecting-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 4px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8fafc;
  border-radius: 8px 8px 0 0;
}

.modal-header h3 {
  margin: 0;
  color: #1f2937;
  font-size: 1.25rem;
  font-weight: 600;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.close-button:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.modal-body {
  padding: 14px;
}

.modal-footer {
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: #f8fafc;
  border-radius: 0 0 8px 8px;
}

.request-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 0;
}

.form-group label {
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
}

.form-input,
.form-select {
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: all 0.2s;
  background-color: white;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-select {
  cursor: pointer;
}

.form-hint {
  color: #6b7280;
  font-size: 0.75rem;
  margin-top: 4px;
}

.form-hint_error {
  color: #dc2626;
}

.btn-primary,
.btn-secondary {
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2563eb;
}

.btn-primary:disabled {
  background-color: #93c5fd;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background-color: #e5e7eb;
}

.search-results {
  margin-top: 20px;
  border-top: 1px solid #e5e7eb;
  padding-top: 16px;
}

.search-empty {
  color: #6b7280;
  font-size: 0.875rem;
  text-align: center;
  margin: 0;
}

.results-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.results-table th {
  text-align: left;
  padding: 8px;
  border-bottom: 2px solid #e5e7eb;
  color: #374151;
  font-weight: 600;
}

.results-table td {
  padding: 8px;
  border-bottom: 1px solid #f3f4f6;
}

.results-table__row {
  cursor: pointer;
  transition: background-color 0.15s;
}

.results-table__row:hover {
  background-color: #f8fafc;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

@media (max-width: 640px) {
  .modal-container {
    width: 95%;
    margin: 10px;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 16px;
  }
}
</style>
